export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_ORIGINAL_BYTES = 8 * 1024 * 1024;
export const MAX_IMAGES = 5;
export const MAX_EDGE = 1024;

export interface PreparedImage {
  id: string;
  name: string;
  blob: Blob;
  previewUrl: string;
}

export const validateImageFile = (file: File) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    return 'Use a JPEG, PNG or WebP image.';
  }
  if (file.size > MAX_ORIGINAL_BYTES) return 'Each original image must be 8 MB or smaller.';
  return null;
};

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The image could not be decoded.'));
    };
    image.src = url;
  });

export const prepareImage = async (file: File): Promise<PreparedImage> => {
  const validation = validateImageFile(file);
  if (validation) throw new Error(validation);
  const image = await loadImage(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot prepare images.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.84));
  if (!blob) throw new Error('This browser could not compress the image.');
  return {
    id: crypto.randomUUID(),
    name: file.name,
    blob,
    previewUrl: URL.createObjectURL(blob),
  };
};

export const disposePreparedImages = (images: PreparedImage[]) => {
  images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
};
